import { useEffect, useState } from 'react';
import type { AssetClassification, InfoAssetPayload, InfoAssetType } from './types';
import {
  ASSET_CLASSIFICATION_LABEL,
  INFO_ASSET_TYPE_LABEL,
} from './assetsUtils';

const TYPES = Object.keys(INFO_ASSET_TYPE_LABEL) as InfoAssetType[];
const CLASSIFICATIONS = Object.keys(ASSET_CLASSIFICATION_LABEL) as AssetClassification[];

interface AssetFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: InfoAssetPayload) => Promise<void>;
  initial?: Partial<InfoAssetPayload>;
  title: string;
}

const defaultPayload: InfoAssetPayload = {
  name: '',
  type: 'informacion',
  owner: '',
  classification: 'interna',
  ciaConfidentiality: 2,
  ciaIntegrity: 2,
  ciaAvailability: 2,
};

export function AssetFormModal({ open, onClose, onSubmit, initial, title }: AssetFormModalProps) {
  const [form, setForm] = useState<InfoAssetPayload>({ ...defaultPayload, ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ ...defaultPayload, ...initial });
      setError(null);
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form);
      onClose();
    } catch {
      setError('No se pudo guardar el activo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        className="bg-card border border-border rounded-lg w-full max-w-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <h2 className="text-lg font-semibold">{title}</h2>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <label className="block text-sm space-y-1">
          <span>Nombre</span>
          <input
            className="input w-full"
            required
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Ej: Base de datos de clientes"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm space-y-1">
            <span>Tipo</span>
            <select
              className="input w-full"
              value={form.type}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, type: e.target.value as InfoAssetType }))
              }
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {INFO_ASSET_TYPE_LABEL[type]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm space-y-1">
            <span>Clasificación</span>
            <select
              className="input w-full"
              value={form.classification}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  classification: e.target.value as AssetClassification,
                }))
              }
            >
              {CLASSIFICATIONS.map((cls) => (
                <option key={cls} value={cls}>
                  {ASSET_CLASSIFICATION_LABEL[cls]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block text-sm space-y-1">
          <span>Propietario</span>
          <input
            className="input w-full"
            required
            value={form.owner}
            onChange={(e) => setForm((prev) => ({ ...prev, owner: e.target.value }))}
            placeholder="Ej: Gerencia de TI, RRHH…"
          />
        </label>

        <div className="grid grid-cols-3 gap-3">
          {(['ciaConfidentiality', 'ciaIntegrity', 'ciaAvailability'] as const).map((field, idx) => (
            <label key={field} className="block text-sm space-y-1">
              <span>{['C', 'I', 'A'][idx]} (1-3)</span>
              <input
                type="number"
                min={1}
                max={3}
                className="input w-full"
                required
                value={form[field]}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, [field]: Number(e.target.value) }))
                }
                placeholder="1–3"
              />
            </label>
          ))}
        </div>

        <label className="block text-sm space-y-1">
          <span>Ubicación (opcional)</span>
          <input
            className="input w-full"
            value={form.location ?? ''}
            onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value || null }))}
            placeholder="Ej: Servidor on-premise, nube AWS…"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
